import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, ImageUp, Trash2 } from 'lucide-react';
import {
  ORG_ABOUT_MAX,
  ORG_SOCIAL_LABELS,
  ORG_SOCIAL_NETWORKS,
  ORG_TAGLINE_MAX,
  formatPhone,
  type OrgSocialNetwork,
} from '@ready/shared';
import { useApi, type OrgProfileSettings } from '@/lib/api';
import { LogoCropper, loadLogoImage } from '@/components/logo-cropper';
import { OrgLogo, VerifiedBadge } from '@/components/org-brand';
import { Button, Card, ErrorText, Field, GuidanceNote, Input, Textarea } from '@/components/ui';
import { useOrgContext } from './layout';

const SITE = 'ready.nirvik.ai';

/**
 * What the org says about itself on its profile (/o/:slug): logo,
 * description, links and contact. Admins edit; parent admins read.
 */
export function OrgProfilePage() {
  const { org, canManage } = useOrgContext();
  const api = useApi();
  const settings = useQuery({
    queryKey: ['orgProfile', org.id],
    queryFn: () => api.orgProfileSettings(org.id),
  });
  if (settings.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (settings.isError) return <ErrorText error={settings.error} />;
  const s = settings.data;
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Your public page at{' '}
          <span className="font-mono text-foreground">
            {SITE}/o/{s.slug}
          </span>
          {s.verified && (
            <span className="ml-2 inline-flex items-center gap-1 align-middle text-primary">
              <VerifiedBadge /> Verified
            </span>
          )}
        </p>
        <Link
          to={`/o/${s.slug}`}
          className="-my-2 inline-flex items-center gap-1.5 py-2 text-sm font-medium text-primary hover:underline"
        >
          View profile <ExternalLink className="size-4" aria-hidden />
        </Link>
      </div>
      {s.hidden && (
        <GuidanceNote>
          The Nirvik Ready team has hidden this profile from the public. Members can still see it.
          Contact Support if you think this is a mistake.
        </GuidanceNote>
      )}
      <LogoCard orgId={org.id} name={org.name} logoUrl={s.logoUrl} canManage={canManage} />
      <DetailsForm orgId={org.id} settings={s} canManage={canManage} />
    </div>
  );
}

function LogoCard({
  orgId,
  name,
  logoUrl,
  canManage,
}: {
  orgId: string;
  name: string;
  logoUrl: string | null;
  canManage: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<HTMLImageElement | null>(null);
  const [pickError, setPickError] = useState<Error | null>(null);
  const refresh = () => {
    for (const key of [['orgProfile', orgId], ['org', orgId], ['myOrgs']])
      queryClient.invalidateQueries({ queryKey: key });
  };
  const upload = useMutation({
    mutationFn: (png: Blob) => api.uploadOrgLogo(orgId, png),
    onSuccess: () => {
      setPicked(null);
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: () => api.removeOrgLogo(orgId), onSuccess: refresh });

  return (
    <Card title="Logo">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <OrgLogo name={name} url={logoUrl} size="xl" />
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            A square image, at least 200 × 200 pixels (PNG, JPEG or WebP, up to 2 MB). It shows on
            your profile, next to your courses, quizzes and events, and on certificates and posters.
          </p>
          {canManage && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  setPickError(null);
                  upload.reset();
                  try {
                    setPicked(await loadLogoImage(file));
                  } catch (err) {
                    setPickError(err as Error);
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => input.current?.click()}
              >
                <ImageUp className="size-4" aria-hidden />
                {logoUrl ? 'Replace logo' : 'Upload logo'}
              </Button>
              {logoUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full sm:w-auto"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  <Trash2 className="size-4" aria-hidden />
                  Remove
                </Button>
              )}
            </div>
          )}
          <ErrorText error={pickError ?? remove.error ?? (picked ? null : upload.error)} />
        </div>
      </div>
      {picked && (
        <LogoCropper
          image={picked}
          busy={upload.isPending}
          onCancel={() => setPicked(null)}
          onDone={(png) => upload.mutate(png)}
        />
      )}
      {picked && upload.error && <ErrorText error={upload.error} />}
    </Card>
  );
}

type Form = {
  slug: string;
  tagline: string;
  about: string;
  websiteUrl: string;
  contactEmail: string;
  contactPhone: string;
  city: string;
  socialLinks: Record<OrgSocialNetwork, string>;
  profilePublic: boolean;
  contactPublic: boolean;
};

const toForm = (s: OrgProfileSettings): Form => ({
  slug: s.slug,
  tagline: s.tagline ?? '',
  about: s.about ?? '',
  websiteUrl: s.websiteUrl ?? '',
  contactEmail: s.contactEmail ?? '',
  contactPhone: s.contactPhone ? formatPhone(s.contactPhone) : '',
  city: s.city ?? '',
  socialLinks: Object.fromEntries(
    ORG_SOCIAL_NETWORKS.map((n) => [n, s.socialLinks[n] ?? '']),
  ) as Form['socialLinks'],
  profilePublic: s.profilePublic,
  contactPublic: s.contactPublic,
});

function DetailsForm({
  orgId,
  settings,
  canManage,
}: {
  orgId: string;
  settings: OrgProfileSettings;
  canManage: boolean;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => toForm(settings));
  useEffect(() => setForm(toForm(settings)), [settings]);
  const set = <K extends keyof Form>(key: K, value: Form[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const save = useMutation({
    mutationFn: () => api.updateOrgProfile(orgId, form),
    onSuccess: (saved) => {
      queryClient.setQueryData(['orgProfile', orgId], saved);
      queryClient.invalidateQueries({ queryKey: ['org', orgId] });
      queryClient.invalidateQueries({ queryKey: ['myOrgs'] });
    },
  });
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(settings));
  const disabled = !canManage;

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <fieldset disabled={disabled} className="space-y-6">
        <Card title="About">
          <div className="space-y-4">
            <Field
              label="Short description"
              htmlFor="p-tagline"
              hint={`Shown under your name on cards and link previews. ${form.tagline.length} / ${ORG_TAGLINE_MAX}`}
            >
              <Input
                id="p-tagline"
                maxLength={ORG_TAGLINE_MAX}
                value={form.tagline}
                placeholder="Preparing every student for a safer digital world"
                onChange={(e) => set('tagline', e.target.value)}
              />
            </Field>
            <Field
              label="About"
              htmlFor="p-about"
              hint={`Who you are and what you offer. Markdown works for **bold**, lists and links. ${form.about.length.toLocaleString('en-US')} / ${ORG_ABOUT_MAX.toLocaleString('en-US')}`}
            >
              <Textarea
                id="p-about"
                rows={6}
                maxLength={ORG_ABOUT_MAX}
                value={form.about}
                onChange={(e) => set('about', e.target.value)}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Website" htmlFor="p-web">
                <Input
                  id="p-web"
                  type="url"
                  inputMode="url"
                  placeholder="https://example.edu"
                  value={form.websiteUrl}
                  onChange={(e) => set('websiteUrl', e.target.value)}
                />
              </Field>
              <Field label="City or region" htmlFor="p-city">
                <Input
                  id="p-city"
                  maxLength={80}
                  value={form.city}
                  placeholder="Hyderabad"
                  onChange={(e) => set('city', e.target.value)}
                />
              </Field>
            </div>
          </div>
        </Card>

        <Card title="Contact">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email" htmlFor="p-email">
                <Input
                  id="p-email"
                  type="email"
                  inputMode="email"
                  placeholder="learning@example.edu"
                  value={form.contactEmail}
                  onChange={(e) => set('contactEmail', e.target.value)}
                />
              </Field>
              <Field label="Phone" htmlFor="p-phone" hint="With the country code.">
                <Input
                  id="p-phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="+91 98765 43210"
                  value={form.contactPhone}
                  onChange={(e) => set('contactPhone', e.target.value)}
                />
              </Field>
            </div>
            <Check
              checked={form.contactPublic}
              onChange={(v) => set('contactPublic', v)}
              label="Show email and phone to everyone"
              hint="Otherwise only your members see them."
            />
          </div>
        </Card>

        <Card title="Social links">
          <div className="grid gap-4 sm:grid-cols-2">
            {ORG_SOCIAL_NETWORKS.map((n) => (
              <Field key={n} label={ORG_SOCIAL_LABELS[n]} htmlFor={`p-${n}`}>
                <Input
                  id={`p-${n}`}
                  type="url"
                  inputMode="url"
                  placeholder={SOCIAL_PLACEHOLDERS[n]}
                  value={form.socialLinks[n]}
                  onChange={(e) => set('socialLinks', { ...form.socialLinks, [n]: e.target.value })}
                />
              </Field>
            ))}
          </div>
        </Card>

        <Card title="Visibility">
          <div className="space-y-4">
            <Check
              checked={form.profilePublic}
              onChange={(v) => set('profilePublic', v)}
              label="Anyone can see this profile"
              hint="It's listed in the organization directory, and your name links to it from your public courses, quizzes and events. Otherwise only your members can open it."
            />
            <Field
              label="Profile address"
              htmlFor="p-slug"
              hint="Lowercase letters, numbers and hyphens. Old addresses keep working."
            >
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-sm text-muted-foreground">{SITE}/o/</span>
                <Input
                  id="p-slug"
                  required
                  minLength={3}
                  maxLength={60}
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  value={form.slug}
                  onChange={(e) => set('slug', e.target.value.toLowerCase())}
                />
              </div>
            </Field>
          </div>
        </Card>
      </fieldset>

      {canManage && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button type="submit" className="w-full sm:w-auto" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save profile'}
          </Button>
          {save.isSuccess && !dirty && (
            <p className="text-sm text-muted-foreground" role="status">
              Saved.
            </p>
          )}
        </div>
      )}
      <ErrorText error={save.error} />
    </form>
  );
}

const SOCIAL_PLACEHOLDERS: Record<OrgSocialNetwork, string> = {
  linkedin: 'https://www.linkedin.com/company/…',
  x: 'https://x.com/…',
  youtube: 'https://www.youtube.com/@…',
  instagram: 'https://www.instagram.com/…',
  facebook: 'https://www.facebook.com/…',
};

function Check({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint?: ReactNode;
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 size-5 shrink-0 accent-primary"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="font-medium">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}
